import { DeleteChatDialog } from '@/app/_common/delete-chat-dialog'
import { DialogInputForm } from '@/app/_common/dialog-input-form'
import { CopyIcon } from '@/components/icons/copy-icon'
import { EditIcon } from '@/components/icons/edit-icon'
import { TrashIcon } from '@/components/icons/trash-icon'
import { Button, ButtonProps } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { toast } from '@/components/ui/toaster'
import { tooltipIconButtonVariants } from '@/components/ui/tooltip-icon-button'
import { useWorkflowTitleMutation } from '@/hooks/api/use-set-workflow-title-mutation'
import { cn, copy } from '@/lib/utils'
import { DropdownMenuContentProps } from '@radix-ui/react-dropdown-menu'
import { useTranslate } from '@tolgee/react'
import { EllipsisVertical, XSquareIcon } from 'lucide-react'
import React from 'react'
import { useSplitView } from './split-view/split-view-context'
import { isChatContextTab } from './split-view/tabs/types'

type ChatOptionsMenuProps = Readonly<
  {
    workflowId: string
    workflowTitle?: string
    className?: string
    asChild?: boolean
    includeCloseTab?: boolean
  } & Pick<DropdownMenuContentProps, 'align' | 'alignOffset'>
>

function ChatOptionsMenu({
  workflowId,
  workflowTitle,
  className,
  align = 'start',
  alignOffset,
  asChild,
  includeCloseTab,
}: ChatOptionsMenuProps) {
  const { t } = useTranslate()
  const TriggerChild = asChild ? 'span' : React.Fragment
  const [open, setOpen] = React.useState(false)
  const [openDialogId, setOpenDialogId] = React.useState<'title' | 'delete' | 'chatID' | null>(null)
  const { filterTabs } = useSplitView()

  const updateTitleMutation = useWorkflowTitleMutation({
    onSuccess: () => toast.success('Chat title updated'),
    onError: (e) => {
      console.error(e)
      toast.error('Failed to update chat title')
    },
  })

  const openRenameDialog = () => {
    setOpenDialogId('title')
    setOpen(false)
  }

  const openDeleteDialog = () => {
    setOpenDialogId('delete')
    setOpen(false)
  }

  const handleCopy = () => {
    copy({
      text: workflowId,
      successMessage: t('chat.idDialog.copy.success'),
      errorMessage: t('chat.idDialog.copy.error'),
    })
  }

  const handleCloseTab = () => {
    filterTabs({
      predicate: (t) => isChatContextTab(t) && t.workflowId === workflowId,
    })
  }

  return (
    <>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger
          className={cn(tooltipIconButtonVariants({ size: 'xs' }), className)}
          title='Chat options menu'
          asChild={asChild}
        >
          <TriggerChild>
            <EllipsisVertical className='size-4 text-neutral-150' />
          </TriggerChild>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align={align}
          alignOffset={alignOffset}
          className='mt-1 w-36 border border-white bg-neutral-25 p-1.5 shadow-sidebar'
          onPointerDown={(e) => {
            if (asChild) e.stopPropagation()
          }}
        >
          {workflowId && (
            <DropdownMenuItem asChild unstyled>
              <ChatOptionButton
                onClick={handleCopy}
                icon={<CopyIcon className='shrink-0 text-neutral-150' />}
                title={t('history.workflowsTable.copyID.title')}
              />
            </DropdownMenuItem>
          )}
          <DropdownMenuItem asChild unstyled>
            <ChatOptionButton
              onClick={openRenameDialog}
              icon={<EditIcon className='shrink-0 text-neutral-150' />}
              title={t('history.workflowsTable.edit.title')}
            />
          </DropdownMenuItem>
          {includeCloseTab && (
            <DropdownMenuItem asChild unstyled>
              <ChatOptionButton
                onClick={handleCloseTab}
                icon={
                  <XSquareIcon strokeWidth={1.75} className='size-4 shrink-0 text-neutral-150' />
                }
                title='Close Tab'
              />
            </DropdownMenuItem>
          )}
          <DropdownMenuItem asChild unstyled>
            <ChatOptionButton
              className='text-error-300'
              onClick={openDeleteDialog}
              icon={<TrashIcon className='shrink-0' />}
              title={t('history.workflowsTable.delete.title')}
            />
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <DialogInputForm
        title='Rename Chat'
        descriptionHidden='Update chat title'
        submitButtonText='Save'
        initialValue={workflowTitle}
        isLoading={updateTitleMutation.isPending}
        onSubmit={({ inputValue }) => updateTitleMutation.mutate({ workflowId, title: inputValue })}
        openState={{
          open: openDialogId === 'title',
          onOpenChange: (value) => setOpenDialogId(value ? 'title' : null),
        }}
      />
      <DeleteChatDialog
        open={openDialogId === 'delete'}
        onOpenChange={(value) => setOpenDialogId(value ? 'delete' : null)}
        workflowId={workflowId}
        workflowTitle={workflowTitle ?? null}
      />
    </>
  )
}

const ChatOptionButton = React.forwardRef<
  HTMLButtonElement,
  ButtonProps &
    Readonly<{
      icon: React.ReactNode
      title: string
    }>
>(({ icon, title, children, className, ...props }, ref) => {
  return (
    <Button
      ref={ref}
      unstyled
      title={title}
      className={cn(
        'flex h-10 w-full select-none items-center gap-3 rounded px-2 text-sm font-light tracking-wide outline-none hover:bg-neutral-75/75 focus:bg-neutral-75/75',
        className,
      )}
      {...props}
    >
      <span className='flex w-5 shrink-0 items-center justify-center'>{icon}</span>
      {title}
      {children}
    </Button>
  )
})
ChatOptionButton.displayName = 'ChatOptionButton'

export { ChatOptionsMenu, type ChatOptionsMenuProps }
